import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';

const Base64EncoderPage = () => {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'text';

  const [textInput, setTextInput] = useState('');
  const [textOutput, setTextOutput] = useState('');
  const [imageInput, setImageInput] = useState(null);
  const [imageOutput, setImageOutput] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [mode, setMode] = useState('encode'); // 'encode' or 'decode'
  const [tab, setTab] = useState(initialTab); // 'text', 'image', or 'confluence'
  const [confluenceEmail, setConfluenceEmail] = useState('');
  const [confluenceToken, setConfluenceToken] = useState('');
  const [confluenceOutput, setConfluenceOutput] = useState('');
  const [autoUpdate, setAutoUpdate] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [urlSafe, setUrlSafe] = useState(false);
  const [includeDataUri, setIncludeDataUri] = useState(true);
  const fileInputRef = useRef(null);
  const confluenceEmailRef = useRef(null);

  // Text encoding/decoding
  const encodeText = (text) => {
    try {
      setError('');
      // Handle Unicode characters properly
      const utf8Bytes = new TextEncoder().encode(text);
      let binary = '';
      utf8Bytes.forEach(byte => {
        binary += String.fromCharCode(byte);
      });
      let result = btoa(binary);
      
      if (urlSafe) {
        result = result.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      }
      
      return result;
    } catch (e) {
      setError('Error encoding: ' + e.message);
      return '';
    }
  };

  const decodeText = (text) => {
    try {
      setError('');
      let input = text.trim();
      
      // Handle URL-safe Base64
      if (urlSafe || input.includes('-') || input.includes('_')) {
        input = input.replace(/-/g, '+').replace(/_/g, '/');
        // Add padding if necessary
        while (input.length % 4) {
          input += '=';
        }
      }
      
      const binary = atob(input);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new TextDecoder().decode(bytes);
    } catch (e) {
      setError('Error decoding: Invalid Base64 string. ' + e.message);
      return '';
    }
  };

  // Process text input
  const processTextInput = () => {
    if (!textInput) {
      setTextOutput('');
      setError('');
      return;
    }

    if (mode === 'encode') {
      setTextOutput(encodeText(textInput));
    } else {
      setTextOutput(decodeText(textInput));
    }
  };

  // Auto-update for text
  useEffect(() => {
    if (autoUpdate && tab === 'text') {
      const timeoutId = setTimeout(() => {
        processTextInput();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textInput, mode, autoUpdate, urlSafe, tab]);

  // Auto-update for Confluence
  useEffect(() => {
    if (confluenceEmail && confluenceToken) {
      const combined = `${confluenceEmail}:${confluenceToken}`;
      try {
        const utf8Bytes = new TextEncoder().encode(combined);
        let binary = '';
        utf8Bytes.forEach(byte => {
          binary += String.fromCharCode(byte);
        });
        setConfluenceOutput(btoa(binary));
      } catch (e) {
        setConfluenceOutput('');
      }
    } else {
      setConfluenceOutput('');
    }
  }, [confluenceEmail, confluenceToken]);

  // Auto-focus email input when Confluence tab is selected
  useEffect(() => {
    if (tab === 'confluence' && confluenceEmailRef.current) {
      confluenceEmailRef.current.focus();
    }
  }, [tab]);

  // Handle image file selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setImageInput(null);
      setImageOutput('');
      setImagePreview('');
      return;
    }

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setError('');
    setImageInput(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target.result;
      setImagePreview(dataUrl);
      
      if (includeDataUri) {
        setImageOutput(dataUrl);
      } else {
        // Remove data URI prefix
        setImageOutput(dataUrl.split(',')[1]);
      }
    };
    reader.readAsDataURL(file);
  };

  // Decode Base64 to image
  const decodeToImage = () => {
    if (!textInput.trim()) {
      setError('Please enter a Base64 string');
      return;
    }

    try {
      setError('');
      let base64 = textInput.trim();
      
      // Check if it already has data URI prefix
      if (!base64.startsWith('data:')) {
        // Try to detect image type from Base64
        let mimeType = 'image/png'; // default
        
        if (base64.startsWith('/9j/')) {
          mimeType = 'image/jpeg';
        } else if (base64.startsWith('iVBOR')) {
          mimeType = 'image/png';
        } else if (base64.startsWith('R0lGOD')) {
          mimeType = 'image/gif';
        } else if (base64.startsWith('UklGR')) {
          mimeType = 'image/webp';
        }
        
        base64 = `data:${mimeType};base64,${base64}`;
      }
      
      setImagePreview(base64);
    } catch (e) {
      setError('Invalid Base64 image data: ' + e.message);
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Download decoded image
  const downloadImage = () => {
    if (!imagePreview) return;
    
    const link = document.createElement('a');
    link.href = imagePreview;
    link.download = `decoded-image-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Clear all
  const clearAll = () => {
    setTextInput('');
    setTextOutput('');
    setImageInput(null);
    setImageOutput('');
    setImagePreview('');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Swap input and output
  const swapTextInputOutput = () => {
    const temp = textInput;
    setTextInput(textOutput);
    setTextOutput(temp);
    setMode(mode === 'encode' ? 'decode' : 'encode');
  };

  // Sample texts
  const sampleTexts = [
    { label: 'Hello World', value: 'Hello, World!' },
    { label: 'JSON Data', value: '{"name":"John","age":30,"city":"New York"}' },
    { label: 'Unicode', value: '日本語テスト 🎉' },
    { label: 'HTML', value: '<h1>Hello</h1><p>This is a test.</p>' },
    { label: 'Long Text', value: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.' }
  ];

  return (
    <div>
      <SEO 
        title="Base64 Encoder/Decoder - Text and Image Base64 Conversion" 
        description="Free online Base64 encoder and decoder. Convert text to Base64, decode Base64 strings, and convert images to Base64 data URIs. Supports Unicode and URL-safe encoding."
        keywords="base64 encoder, base64 decoder, base64 image, image to base64, base64 converter, base64 online, data uri, base64 text"
      />
      
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Base64 Encoder / Decoder</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Encode text to Base64, decode Base64 strings, or convert images to Base64 data URIs. All processing happens locally.
        </p>
        
        {/* Tab Selection */}
        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #e0e0e0' }}>
            <button
              onClick={() => setTab('text')}
              style={{
                padding: '12px 30px',
                border: 'none',
                borderBottom: tab === 'text' ? '2px solid #2196f3' : '2px solid transparent',
                backgroundColor: 'transparent',
                color: tab === 'text' ? '#2196f3' : '#666',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: tab === 'text' ? '600' : '400',
                marginBottom: '-2px',
                transition: 'all 0.2s ease'
              }}
            >
              📝 Text
            </button>
            <button
              onClick={() => setTab('image')}
              style={{
                padding: '12px 30px',
                border: 'none',
                borderBottom: tab === 'image' ? '2px solid #2196f3' : '2px solid transparent',
                backgroundColor: 'transparent',
                color: tab === 'image' ? '#2196f3' : '#666',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: tab === 'image' ? '600' : '400',
                marginBottom: '-2px',
                transition: 'all 0.2s ease'
              }}
            >
              🖼️ Image
            </button>
            <button
              onClick={() => setTab('confluence')}
              style={{
                padding: '12px 30px',
                border: 'none',
                borderBottom: tab === 'confluence' ? '2px solid #2196f3' : '2px solid transparent',
                backgroundColor: 'transparent',
                color: tab === 'confluence' ? '#2196f3' : '#666',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: tab === 'confluence' ? '600' : '400',
                marginBottom: '-2px',
                transition: 'all 0.2s ease'
              }}
            >
              🔐 Confluence
            </button>
          </div>
        </div>

        {/* Text Tab */}
        {tab === 'text' && (
          <>
            {/* Mode Toggle */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  onClick={() => setMode('encode')}
                  style={{
                    flex: '0 0 200px',
                    padding: '14px 24px',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: mode === 'encode' ? '#2196f3' : '#e0e0e0',
                    color: mode === 'encode' ? 'white' : '#333',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                  }}
                >
                  🔒 Encode
                </button>
                <button
                  onClick={swapTextInputOutput}
                  style={{
                    padding: '14px 20px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    color: '#333',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                  title="Swap input and output"
                >
                  ⇄
                </button>
                <button
                  onClick={() => setMode('decode')}
                  style={{
                    flex: '0 0 200px',
                    padding: '14px 24px',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: mode === 'decode' ? '#4caf50' : '#e0e0e0',
                    color: mode === 'decode' ? 'white' : '#333',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                  }}
                >
                  🔓 Decode
                </button>
              </div>
            </div>

            {/* Options */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={urlSafe}
                  onChange={(e) => setUrlSafe(e.target.checked)}
                  style={{ marginRight: '8px', width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '14px' }}>URL-safe encoding (use - and _ instead of + and /)</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={autoUpdate}
                  onChange={(e) => setAutoUpdate(e.target.checked)}
                  style={{ marginRight: '8px', width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '14px' }}>Auto-update</span>
              </label>
            </div>

            {/* Text Input/Output */}
            <div className="main-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* Input */}
              <div className="input-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={{ fontWeight: '500' }}>
                    {mode === 'encode' ? 'Text to Encode:' : 'Base64 to Decode:'}
                  </label>
                  <span style={{ fontSize: '12px', color: '#666' }}>{textInput.length} characters</span>
                </div>
                <textarea
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder={mode === 'encode' 
                    ? 'Enter text to encode to Base64...' 
                    : 'Enter Base64 string to decode...'}
                  style={{
                    width: '100%',
                    height: '250px',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
                
                {/* Sample Texts */}
                <div style={{ marginTop: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                    Quick Examples:
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {sampleTexts.map((sample, index) => (
                      <button
                        key={index}
                        onClick={() => setTextInput(mode === 'encode' ? sample.value : encodeText(sample.value))}
                        style={{
                          padding: '4px 10px',
                          border: '1px solid #ddd',
                          borderRadius: '12px',
                          backgroundColor: 'white',
                          color: '#333',
                          cursor: 'pointer',
                          fontSize: '11px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                      >
                        {sample.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Output */}
              <div className="output-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={{ fontWeight: '500' }}>
                    {mode === 'encode' ? 'Base64 Result:' : 'Decoded Text:'}
                  </label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', color: '#666' }}>{textOutput.length} characters</span>
                    {textOutput && (
                      <button
                        onClick={() => copyToClipboard(textOutput)}
                        style={{
                          padding: '4px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: copied ? '#4caf50' : 'white',
                          color: copied ? 'white' : '#333',
                          cursor: 'pointer',
                          fontSize: '12px',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {copied ? '✓ Copied!' : '📋 Copy'}
                      </button>
                    )}
                  </div>
                </div>
                <textarea
                  value={textOutput}
                  readOnly
                  placeholder={mode === 'encode' 
                    ? 'Base64 encoded result will appear here...' 
                    : 'Decoded text will appear here...'}
                  style={{
                    width: '100%',
                    height: '250px',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    backgroundColor: '#f9f9f9'
                  }}
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div style={{
                marginTop: '15px',
                padding: '10px',
                backgroundColor: '#ffebee',
                border: '1px solid #f44336',
                borderRadius: '4px',
                color: '#c62828',
                fontSize: '13px',
                textAlign: 'center'
              }}>
                ⚠️ {error}
              </div>
            )}

            {/* Actions */}
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={processTextInput}
                style={{
                  padding: '12px 24px',
                  backgroundColor: mode === 'encode' ? '#2196f3' : '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {mode === 'encode' ? '🔒 Encode' : '🔓 Decode'}
              </button>
              
              <button
                onClick={clearAll}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                🗑️ Clear
              </button>
            </div>
          </>
        )}

        {/* Image Tab */}
        {tab === 'image' && (
          <div className="main-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
            {/* Image to Base64 */}
            <div className="encode-section">
              <h2 style={{ fontSize: '1.3rem', marginBottom: '15px' }}>Image → Base64</h2>
              
              {/* Options */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={includeDataUri}
                    onChange={(e) => setIncludeDataUri(e.target.checked)}
                    style={{ marginRight: '8px', width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Include data URI prefix (data:image/...)</span>
                </label>
              </div>
              
              {/* File Input */}
              <div
                style={{
                  border: '2px dashed #ddd',
                  borderRadius: '8px',
                  padding: '30px 20px',
                  textAlign: 'center',
                  backgroundColor: '#fafafa',
                  cursor: 'pointer',
                  marginBottom: '15px'
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                />
                {imageInput ? (
                  <div>
                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>🖼️</div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>{imageInput.name}</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                      {(imageInput.size / 1024).toFixed(2)} KB
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: '48px', marginBottom: '10px', opacity: 0.3 }}>📷</div>
                    <div style={{ fontSize: '14px', color: '#666' }}>Click to select an image</div>
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>PNG, JPG, GIF, WebP</div>
                  </div>
                )}
              </div>

              {/* Image Preview */}
              {imagePreview && (
                <div style={{ marginBottom: '15px', textAlign: 'center' }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '150px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              )}

              {/* Base64 Output */}
              {imageOutput && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ fontWeight: '500', fontSize: '14px' }}>Base64 Output:</label>
                    <button
                      onClick={() => copyToClipboard(imageOutput)}
                      style={{
                        padding: '4px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: copied ? '#4caf50' : 'white',
                        color: copied ? 'white' : '#333',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      {copied ? '✓ Copied!' : '📋 Copy'}
                    </button>
                  </div>
                  <textarea
                    value={imageOutput}
                    readOnly
                    style={{
                      width: '100%',
                      height: '150px',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontFamily: 'monospace',
                      boxSizing: 'border-box',
                      backgroundColor: '#f9f9f9'
                    }}
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    {imageOutput.length} characters
                  </div>
                </div>
              )}
            </div>

            {/* Base64 to Image */}
            <div className="decode-section">
              <h2 style={{ fontSize: '1.3rem', marginBottom: '15px' }}>Base64 → Image</h2>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  Paste Base64 string:
                </label>
                <textarea
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Paste Base64 encoded image data here..."
                  style={{
                    width: '100%',
                    height: '150px',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <button
                onClick={decodeToImage}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '15px'
                }}
              >
                🔓 Decode to Image
              </button>

              {/* Decoded Image Preview */}
              {imagePreview && tab === 'image' && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: '10px', fontWeight: '500', fontSize: '14px' }}>Decoded Image:</div>
                  <img
                    src={imagePreview}
                    alt="Decoded"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                    onError={() => setError('Invalid Base64 image data')}
                  />
                  <div style={{ marginTop: '10px' }}>
                    <button
                      onClick={downloadImage}
                      style={{
                        padding: '8px 20px',
                        backgroundColor: '#2196f3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      📥 Download Image
                    </button>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div style={{
                  marginTop: '15px',
                  padding: '10px',
                  backgroundColor: '#ffebee',
                  border: '1px solid #f44336',
                  borderRadius: '4px',
                  color: '#c62828',
                  fontSize: '13px'
                }}>
                  ⚠️ {error}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Confluence Tab */}
        {tab === 'confluence' && (
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
                Confluence API authentication için email ve token'ı birleştirip Base64 encode eder.
                <br />
                Format: <code style={{ backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>email:token</code> → Base64
              </p>
            </div>

            {/* Email Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Email:
              </label>
              <input
                ref={confluenceEmailRef}
                type="email"
                value={confluenceEmail}
                onChange={(e) => setConfluenceEmail(e.target.value)}
                placeholder="ornek@sirket.com"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Token Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                API Token:
              </label>
              <input
                type="text"
                value={confluenceToken}
                onChange={(e) => setConfluenceToken(e.target.value)}
                placeholder="ATATT3xFfGF0..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Output */}
            {confluenceOutput && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontWeight: '500' }}>Base64 Output:</label>
                  <button
                    onClick={() => copyToClipboard(confluenceOutput)}
                    style={{
                      padding: '6px 16px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: copied ? '#4caf50' : 'white',
                      color: copied ? 'white' : '#333',
                      cursor: 'pointer',
                      fontSize: '13px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {copied ? '✓ Copied!' : '📋 Copy'}
                  </button>
                </div>
                <textarea
                  value={confluenceOutput}
                  readOnly
                  style={{
                    width: '100%',
                    height: '100px',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    boxSizing: 'border-box',
                    backgroundColor: '#f9f9f9'
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Information Section */}
        <div className="info-section" style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>About Base64 Encoding</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>What is Base64?</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                Base64 is a binary-to-text encoding scheme that represents binary data using 64 ASCII characters
                (A-Z, a-z, 0-9, +, /). It's commonly used to embed binary data in text-based formats like JSON, XML, and HTML.
              </p>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Common Use Cases</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                Embedding images in HTML/CSS (data URIs), encoding email attachments (MIME), 
                transmitting binary data in APIs, storing binary data in JSON, and encoding authentication tokens.
              </p>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>URL-Safe Base64</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                Standard Base64 uses + and / which have special meaning in URLs. URL-safe Base64 replaces these
                with - and _ to make the output safe for use in URLs and filenames.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @media (max-width: 768px) {
          .container {
            padding: 15px;
          }
          
          .main-content {
            grid-template-columns: 1fr !important;
            gap: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default Base64EncoderPage;

